import {
  ActionsMenu,
  Button,
  ListTotal,
  Table,
  TableToolbar,
  TableWrapper,
  Typography,
} from 'melp-design/components';
import { Divider, Stack } from '@mui/material';
import {
  EmailOutlined,
  PhoneOutlined,
  CakeOutlined,
} from '@mui/icons-material';
import moment from 'moment';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  CustomCategoryValuesFilter,
  DateRangeFilter,
  EmployeesFilter,
  EmployeesGroupsFilter,
  FilterButton,
  Filters,
  OpenSearchFilter,
  OrgStructureFilter,
  SelectFilter,
} from 'components/filters';
import { APP } from 'config';
import { useModalContext } from 'store/modal';
import { DownloadXlsAction } from 'components/actions';
import { useMe } from 'state/Administrators';
import {
  COLOR_BY_EMPLOYMENT_STATUS,
  employeesFiltersSchema,
  employmentStatuses,
  useBulkUpdateEmployees,
  useEmployees,
  useExportToXls,
  useSendInvitations,
} from 'store/employees';
import { useSearchParams } from 'utils/navigation';
import { binaries } from 'types/Common';
import { useCustomCategories } from 'store/custom-categories';
import { getTimezoneFromDate } from 'utils/Common';
import { usePredefinedToasts } from 'utils/Toast';
import {
  CreateEmployeeModal,
  CompanyScopeInfoMessage,
  DateSelectModal,
  ImportConsentsModal,
  ImportOrgStructureModal,
  UpdateConsentsModal,
} from 'containers/employees';
import { EmployeesGroupSelectModal } from 'containers/employees-groups';
import { useAssignEmployeesToGroup } from 'store/assignments';
import { EmployeeCell } from 'components/table';
import { CustomCategorySelectModal } from './atoms';

export const EmployeesList: FC = () => {
  const { t } = useTranslation();
  const { canEditEmployees, hasEditPermission } = useMe();
  const { openModal } = useModalContext();
  const predefinedToasts = usePredefinedToasts();
  const { selectedRowIds, setSelectedRowIds } = Table.useSelectedRows();

  const { searchParams: filters, navigate } = useSearchParams(
    employeesFiltersSchema,
  );

  const { data: employees, isLoading } = useEmployees(filters);
  const { data: customCategories } = useCustomCategories();

  const { mutate: bulkUpdateEmployees, isLoading: isBulkUpdating } =
    useBulkUpdateEmployees();
  const { mutate: sendInvitations, isLoading: isSendingInvitations } =
    useSendInvitations();
  const { mutate: assignEmployeesToGroup, isLoading: isAssigning } =
    useAssignEmployeesToGroup();
  const { mutate: exportToXls, isLoading: isExporting } = useExportToXls();

  const selectedCategory = useMemo(
    () =>
      customCategories?.find((c) => c.id === filters.customCategory) ?? null,
    [customCategories, filters.customCategory],
  );

  return (
    <TableWrapper>
      <TableToolbar>
        <Stack gap={1}>
          <CompanyScopeInfoMessage />

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            gap={1}
          >
            <Button
              label={t('employees.new_employee')}
              size="sm"
              disabled={!canEditEmployees || !hasEditPermission(['employees'])}
              onClick={() => {
                openModal({
                  component: CreateEmployeeModal,
                  size: 'lg',
                });
              }}
            />
            <ActionsMenu
              type="compact"
              items={[
                {
                  label: t('employees.consents.actions.import'),
                  disabled:
                    !canEditEmployees || !hasEditPermission(['employees']),
                  onClick: () => {
                    openModal({
                      component: ImportConsentsModal,
                      size: 'lg',
                      props: { filters },
                    });
                  },
                },
                {
                  label: t('employees.org_structure.actions.import'),
                  disabled:
                    !canEditEmployees || !hasEditPermission(['employees']),
                  onClick: () => {
                    openModal({
                      component: ImportOrgStructureModal,
                      size: 'lg',
                      props: { filters },
                    });
                  },
                },
                {
                  label: t('employees.choose_custom_column'),
                  onClick: async () => {
                    const response = await openModal({
                      component: CustomCategorySelectModal,
                      props: {
                        options: customCategories ?? [],
                        selected: filters.customCategory,
                      },
                    });
                    if (response.action === 'CONFIRM') {
                      navigate({
                        params: {
                          customCategory: response.category,
                          customCategoryValues: [],
                        },
                      });
                    }
                  },
                },
              ]}
            />
          </Stack>

          <Divider />

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            gap="10px"
          >
            <ListTotal total={employees?.total} />

            <Stack direction="row" alignItems="center" gap="10px">
              <Filters
                value={filters}
                initialValues={employeesFiltersSchema.parse({})}
                onChange={(v) => navigate({ params: { ...v, page: 1 } })}
                onChangeCallback={() => setSelectedRowIds({})}
                keysToExtractFromTotalCalculation={['customCategory']}
              >
                {({ value, initialValues, setFilter }) => (
                  <>
                    <FilterButton
                      label={t('entity_type.employee')}
                      value={value.employees}
                      initialValue={initialValues.employees}
                      onChange={(v) => setFilter('employees', v)}
                    >
                      {({ value, applyFilter, clearFilter, close }) => (
                        <EmployeesFilter
                          value={{ value }}
                          onApplyFilter={({ value }) => applyFilter(value)}
                          onClearFilter={clearFilter}
                          onCancel={close}
                        />
                      )}
                    </FilterButton>

                    <FilterButton
                      label={t('home.organisationStructure_title')}
                      value={value.orgStructure}
                      initialValue={initialValues.orgStructure}
                      onChange={(v) => setFilter('orgStructure', v)}
                      contentMaxWidth={420}
                    >
                      {({ value, applyFilter, clearFilter, close }) => (
                        <OrgStructureFilter
                          value={value}
                          onApplyFilter={applyFilter}
                          onClearFilter={clearFilter}
                          onCancel={close}
                        />
                      )}
                    </FilterButton>

                    {filters.customCategory && selectedCategory ? (
                      <FilterButton
                        label={selectedCategory.name}
                        value={value.customCategoryValues}
                        initialValue={initialValues.customCategoryValues}
                        onChange={(v) => setFilter('customCategoryValues', v)}
                      >
                        {({
                          value: filterValue,
                          applyFilter,
                          clearFilter,
                          close,
                        }) => (
                          <CustomCategoryValuesFilter
                            value={{ value: filterValue }}
                            onApplyFilter={({ value }) => applyFilter(value)}
                            onClearFilter={clearFilter}
                            onCancel={close}
                            customCategoryId={value.customCategory}
                          />
                        )}
                      </FilterButton>
                    ) : null}

                    <FilterButton
                      label={t('entity_type.employeeGroup')}
                      value={value.employeeGroups}
                      initialValue={initialValues.employeeGroups}
                      onChange={(v) => setFilter('employeeGroups', v)}
                    >
                      {({ value, applyFilter, clearFilter, close }) => (
                        <EmployeesGroupsFilter
                          value={{ value }}
                          onApplyFilter={({ value }) => applyFilter(value)}
                          onClearFilter={clearFilter}
                          onCancel={close}
                        />
                      )}
                    </FilterButton>

                    <FilterButton
                      label={t('employees.start_date')}
                      value={value.hiredAt}
                      initialValue={initialValues.hiredAt}
                      onChange={(v) => setFilter('hiredAt', v)}
                    >
                      {({ value, applyFilter, clearFilter, close }) => (
                        <DateRangeFilter
                          value={value}
                          onApply={applyFilter}
                          onClear={clearFilter}
                          onCancel={close}
                          withTimezone
                        />
                      )}
                    </FilterButton>

                    <FilterButton
                      label={t('employees.employment_status')}
                      value={value.status}
                      initialValue={initialValues.status}
                      onChange={(v) => setFilter('status', v)}
                    >
                      {({ value, applyFilter, clearFilter, close }) => (
                        <SelectFilter
                          value={{ value }}
                          onApplyFilter={({ value }) => applyFilter(value)}
                          onClearFilter={clearFilter}
                          onCancel={close}
                          multiple
                          options={employmentStatuses.map((status) => ({
                            label: t(`status.${status}`),
                            key: status,
                          }))}
                          searchable={false}
                        />
                      )}
                    </FilterButton>

                    <FilterButton
                      label={t('employees.app_user')}
                      value={value.isAppUser}
                      initialValue={initialValues.isAppUser}
                      onChange={(v) => setFilter('isAppUser', v)}
                    >
                      {({ value, applyFilter, clearFilter, close }) => {
                        return (
                          <SelectFilter
                            value={{ value }}
                            onApplyFilter={({ value }) => applyFilter(value)}
                            onClearFilter={clearFilter}
                            onCancel={close}
                            options={binaries.map((binary) => ({
                              key: binary,
                              label: t(`common.${binary}`),
                            }))}
                            searchable={false}
                            multiple={false}
                          />
                        );
                      }}
                    </FilterButton>

                    <FilterButton
                      label={t('home.birthdaysSoon')}
                      value={value.birthdaySoon}
                      initialValue={initialValues.birthdaySoon}
                      onChange={(v) => setFilter('birthdaySoon', v)}
                    >
                      {({ value, applyFilter, clearFilter, close }) => {
                        return (
                          <SelectFilter
                            value={{ value }}
                            onApplyFilter={({ value }) => applyFilter(value)}
                            onClearFilter={clearFilter}
                            onCancel={close}
                            options={binaries.map((binary) => ({
                              key: binary,
                              label: t(`common.${binary}`),
                            }))}
                            searchable={false}
                            multiple={false}
                          />
                        );
                      }}
                    </FilterButton>

                    <FilterButton
                      label={t('home.workAnniversariesSoon')}
                      value={value.workAnniversarySoon}
                      initialValue={initialValues.workAnniversarySoon}
                      onChange={(v) => setFilter('workAnniversarySoon', v)}
                    >
                      {({ value, applyFilter, clearFilter, close }) => {
                        return (
                          <SelectFilter
                            value={{ value }}
                            onApplyFilter={({ value }) => applyFilter(value)}
                            onClearFilter={clearFilter}
                            onCancel={close}
                            options={binaries.map((binary) => ({
                              key: binary,
                              label: t(`common.${binary}`),
                            }))}
                            searchable={false}
                            multiple={false}
                          />
                        );
                      }}
                    </FilterButton>

                    <FilterButton
                      label={t('employees.consents.shareEmail.label')}
                      value={value.emailConsent}
                      initialValue={initialValues.emailConsent}
                      onChange={(v) => setFilter('emailConsent', v)}
                    >
                      {({ value, applyFilter, clearFilter, close }) => {
                        return (
                          <SelectFilter
                            value={{ value }}
                            onApplyFilter={({ value }) => applyFilter(value)}
                            onClearFilter={clearFilter}
                            onCancel={close}
                            options={binaries.map((binary) => ({
                              key: binary,
                              label: t(`common.${binary}`),
                            }))}
                            searchable={false}
                            multiple={false}
                          />
                        );
                      }}
                    </FilterButton>

                    <FilterButton
                      label={t('employees.consents.sharePhoneNumber.label')}
                      value={value.phoneNumberConsent}
                      initialValue={initialValues.phoneNumberConsent}
                      onChange={(v) => setFilter('phoneNumberConsent', v)}
                    >
                      {({ value, applyFilter, clearFilter, close }) => {
                        return (
                          <SelectFilter
                            value={{ value }}
                            onApplyFilter={({ value }) => applyFilter(value)}
                            onClearFilter={clearFilter}
                            onCancel={close}
                            options={binaries.map((binary) => ({
                              key: binary,
                              label: t(`common.${binary}`),
                            }))}
                            searchable={false}
                            multiple={false}
                          />
                        );
                      }}
                    </FilterButton>

                    <FilterButton
                      label={t('employees.consents.shareBirthdate.label')}
                      value={value.birthdateConsent}
                      initialValue={initialValues.birthdateConsent}
                      onChange={(v) => setFilter('birthdateConsent', v)}
                    >
                      {({ value, applyFilter, clearFilter, close }) => {
                        return (
                          <SelectFilter
                            value={{ value }}
                            onApplyFilter={({ value }) => applyFilter(value)}
                            onClearFilter={clearFilter}
                            onCancel={close}
                            options={binaries.map((binary) => ({
                              key: binary,
                              label: t(`common.${binary}`),
                            }))}
                            searchable={false}
                            multiple={false}
                          />
                        );
                      }}
                    </FilterButton>

                    <OpenSearchFilter
                      value={value.search}
                      onApplyFilter={(v) => setFilter('search', v)}
                      placeholder={t('common.search')}
                    />
                  </>
                )}
              </Filters>

              <Divider orientation="vertical" variant="middle" flexItem />

              <DownloadXlsAction
                listLength={employees?.total ?? 0}
                loading={isExporting}
                exportToExcel={() => exportToXls({ filters })}
              />
            </Stack>
          </Stack>
        </Stack>
      </TableToolbar>

      <Table
        data={employees?.items ?? []}
        isLoading={isLoading}
        columns={[
          {
            id: 'fullName',
            header: t('common.full_name'),
            cell: ({ row: { original: employee } }) => {
              return <EmployeeCell employee={employee} />;
            },
            meta: { sort: true },
          },
          {
            id: 'birthDate',
            header: t('employees.birthDate'),
            cell: ({ row: { original: employee } }) => {
              return employee.birthday
                ? moment(employee.birthday).format('l')
                : null;
            },
            meta: { sort: true },
          },
          {
            id: 'customCategory',
            header: selectedCategory?.name,
            cell: ({ row: { original: employee } }) => {
              return (
                employee.customCategories.find(
                  (c) => c.id === selectedCategory?.id,
                )?.value.name ?? ''
              );
            },
            meta: { sort: true },
          },
          {
            id: 'employmentDate',
            header: t('employees.start_date'),
            cell: ({ row: { original: employee } }) => {
              return employee.hireDate
                ? moment(employee.hireDate).format('l')
                : null;
            },
            meta: { sort: true },
          },
          {
            id: 'appUser',
            header: t('employees.app_user'),
            cell: ({ row: { original: employee } }) => {
              return employee.isAppUser ? t('common.yes') : t('common.no');
            },
            meta: { sort: true },
          },
          {
            id: 'appConsents',
            header: t('employees.consents.label'),
            cell: ({ row: { original: employee } }) => {
              return (
                <Stack direction="row" alignItems="center" gap={1}>
                  {employee.consents.shareEmail ? (
                    <EmailOutlined fontSize="small" />
                  ) : null}
                  {employee.consents.sharePhoneNumber ? (
                    <PhoneOutlined fontSize="small" />
                  ) : null}
                  {employee.consents.shareBirthdate ? (
                    <CakeOutlined fontSize="small" />
                  ) : null}
                </Stack>
              );
            },
          },
          {
            id: 'status',
            header: t('common.status'),
            cell: ({ row: { original: employee } }) => {
              return (
                <Typography
                  color={COLOR_BY_EMPLOYMENT_STATUS[employee.employmentStatus]}
                >
                  {t(`status.${employee.employmentStatus}`)}
                </Typography>
              );
            },
            meta: { sort: true },
          },
          {
            id: 'actions',
            header: t('common.actions'),
            cell: ({ row: { original: employee } }) => {
              return (
                <ActionsMenu
                  type="compact"
                  items={[
                    {
                      label: t('actions.activate_now'),
                      disabled:
                        !canEditEmployees || !hasEditPermission(['employees']),
                      onClick: () => {
                        const date = moment().toISOString();
                        bulkUpdateEmployees(
                          {
                            employees: [employee.id],
                            data: {
                              startDate: date,
                              startDateTZ: getTimezoneFromDate(date),
                            },
                          },
                          {
                            onSuccess: () => {
                              predefinedToasts.success.updated();
                            },
                          },
                        );
                      },
                    },
                    {
                      label: t('actions.activate_specific_date'),
                      disabled:
                        !canEditEmployees || !hasEditPermission(['employees']),
                      onClick: async () => {
                        const response = await openModal({
                          component: DateSelectModal,
                        });

                        if (response.action === 'CONFIRM') {
                          bulkUpdateEmployees(
                            {
                              employees: [employee.id],
                              data: {
                                startDate: response.date,
                                startDateTZ: getTimezoneFromDate(response.date),
                              },
                            },
                            {
                              onSuccess: () => {
                                predefinedToasts.success.updated();
                              },
                            },
                          );
                        }
                      },
                    },
                    {
                      label: t('actions.deactivate_now'),
                      disabled:
                        !canEditEmployees || !hasEditPermission(['employees']),
                      onClick: () => {
                        const date = moment().toISOString();
                        bulkUpdateEmployees(
                          {
                            employees: [employee.id],
                            data: {
                              endDate: date,
                              endDateTZ: getTimezoneFromDate(date),
                            },
                          },
                          {
                            onSuccess: () => {
                              predefinedToasts.success.updated();
                            },
                          },
                        );
                      },
                    },
                    {
                      label: t('actions.deactivate_specific_date'),
                      disabled:
                        !canEditEmployees || !hasEditPermission(['employees']),
                      onClick: async () => {
                        const response = await openModal({
                          component: DateSelectModal,
                        });

                        if (response.action === 'CONFIRM') {
                          bulkUpdateEmployees(
                            {
                              employees: [employee.id],
                              data: {
                                endDate: response.date,
                                endDateTZ: getTimezoneFromDate(response.date),
                              },
                            },
                            {
                              onSuccess: () => {
                                predefinedToasts.success.updated();
                              },
                            },
                          );
                        }
                      },
                    },
                    {
                      label: t('actions.invite_again'),
                      disabled:
                        !canEditEmployees || !hasEditPermission(['employees']),
                      onClick: () => {
                        sendInvitations(
                          {
                            employees: [employee.id],
                          },
                          {
                            onSuccess: () => {
                              toast(t('common.reminder_sent_successfully'), {
                                type: 'success',
                              });
                            },
                          },
                        );
                      },
                    },
                    {
                      label: t('actions.change_hire_date'),
                      disabled:
                        !canEditEmployees || !hasEditPermission(['employees']),
                      onClick: async () => {
                        const response = await openModal({
                          component: DateSelectModal,
                        });

                        if (response.action === 'CONFIRM') {
                          bulkUpdateEmployees(
                            {
                              employees: [employee.id],
                              data: {
                                startDate: response.date,
                              },
                            },
                            {
                              onSuccess: () => {
                                predefinedToasts.success.updated();
                              },
                            },
                          );
                        }
                      },
                    },
                  ]}
                />
              );
            },
          },
        ]}
        selection={{
          value: selectedRowIds,
          onChange: setSelectedRowIds,
          isLoading: isBulkUpdating || isSendingInvitations || isAssigning,
          actions: [
            {
              label: t('actions.activate_now'),
              disabled: !hasEditPermission(['employees']),
              onClick: () => {
                const date = moment().toISOString();
                bulkUpdateEmployees(
                  {
                    employees: Object.keys(selectedRowIds),
                    data: {
                      startDate: date,
                      startDateTZ: getTimezoneFromDate(date),
                    },
                  },
                  {
                    onSuccess: () => {
                      predefinedToasts.success.updated();
                      setSelectedRowIds({});
                    },
                  },
                );
              },
            },
            {
              label: t('actions.activate_specific_date'),
              disabled: !hasEditPermission(['employees']),
              onClick: async () => {
                const response = await openModal({
                  component: DateSelectModal,
                });

                if (response.action === 'CONFIRM') {
                  bulkUpdateEmployees(
                    {
                      employees: Object.keys(selectedRowIds),
                      data: {
                        startDate: response.date,
                        startDateTZ: getTimezoneFromDate(response.date),
                      },
                    },
                    {
                      onSuccess: () => {
                        predefinedToasts.success.updated();
                        setSelectedRowIds({});
                      },
                    },
                  );
                }
              },
            },
            {
              label: t('actions.deactivate_now'),
              disabled: !hasEditPermission(['employees']),
              onClick: () => {
                const date = moment().toISOString();
                bulkUpdateEmployees(
                  {
                    employees: Object.keys(selectedRowIds),
                    data: {
                      endDate: date,
                      endDateTZ: getTimezoneFromDate(date),
                    },
                  },
                  {
                    onSuccess: () => {
                      predefinedToasts.success.updated();
                      setSelectedRowIds({});
                    },
                  },
                );
              },
            },
            {
              label: t('actions.deactivate_specific_date'),
              disabled: !hasEditPermission(['employees']),
              onClick: async () => {
                const response = await openModal({
                  component: DateSelectModal,
                });

                if (response.action === 'CONFIRM') {
                  bulkUpdateEmployees(
                    {
                      employees: Object.keys(selectedRowIds),
                      data: {
                        endDate: response.date,
                        endDateTZ: getTimezoneFromDate(response.date),
                      },
                    },
                    {
                      onSuccess: () => {
                        predefinedToasts.success.updated();
                        setSelectedRowIds({});
                      },
                    },
                  );
                }
              },
            },
            {
              label: t('actions.invite_again'),
              disabled: !hasEditPermission(['employees']),
              onClick: () => {
                sendInvitations(
                  {
                    employees: Object.keys(selectedRowIds),
                  },
                  {
                    onSuccess: () => {
                      toast(t('common.reminder_sent_successfully'), {
                        type: 'success',
                      });
                      setSelectedRowIds({});
                    },
                  },
                );
              },
            },
            {
              label: t('actions.change_hire_date'),
              disabled: !hasEditPermission(['employees']),
              onClick: async () => {
                const response = await openModal({
                  component: DateSelectModal,
                });

                if (response.action === 'CONFIRM') {
                  bulkUpdateEmployees(
                    {
                      employees: Object.keys(selectedRowIds),
                      data: {
                        startDate: response.date,
                      },
                    },
                    {
                      onSuccess: () => {
                        predefinedToasts.success.updated();
                        setSelectedRowIds({});
                      },
                    },
                  );
                }
              },
            },
            {
              label: t('employees.consents.actions.update'),
              disabled: !canEditEmployees || !hasEditPermission(['employees']),
              onClick: async () => {
                const response = await openModal({
                  component: UpdateConsentsModal,
                  size: 'lg',
                });

                if (
                  response.action === 'CONFIRM' &&
                  Object.keys(response.consents).length > 0
                ) {
                  bulkUpdateEmployees(
                    {
                      employees: Object.keys(selectedRowIds),
                      data: {
                        consents: response.consents,
                      },
                    },
                    {
                      onSuccess: () => {
                        predefinedToasts.success.updated();
                        setSelectedRowIds({});
                      },
                    },
                  );
                }
              },
            },
            ...(['included', 'excluded'] as const).map((action) => ({
              label: t(`status.mark_selections_as_${action}`),
              disabled: !hasEditPermission(['groups']),
              onClick: async () => {
                const response = await openModal({
                  component: EmployeesGroupSelectModal,
                  props: {
                    title: t(`status.mark_selections_as_${action}`),
                  },
                });

                if (response.action === 'CONFIRM') {
                  assignEmployeesToGroup(
                    {
                      employeeGroupId: response.groupId,
                      ...(action === 'included'
                        ? {
                            action: 'create',
                            data: {
                              employeeIds: Object.keys(selectedRowIds),
                              fromNow: true,
                            },
                          }
                        : {
                            action: 'update',
                            data: {
                              employeeIds: Object.keys(selectedRowIds),
                              toNow: true,
                            },
                          }),
                    },
                    {
                      onSuccess: () => {
                        predefinedToasts.success.updated();
                        setSelectedRowIds({});
                      },
                    },
                  );
                }
              },
            })),
          ],
        }}
        sort={{
          value: filters.sort,
          onChange: (v) => navigate({ params: { sort: v } }),
        }}
        rows={{
          inactive: ({ original: employee }) =>
            employee.employmentStatus === 'ended',
        }}
        pagination={
          employees && {
            count: employees.total,
            page: employees.page,
            pageSize: employees.pageSize,
            pageSizeOptions: APP.pagination.defaults.pageSize,
            onChange: (page, pageSize) => {
              navigate({ params: { page, pageSize } });
            },
          }
        }
      />
    </TableWrapper>
  );
};
